
import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { defaultFunnelData } from "@/components/FunnelBuilder/constants";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useIsMobile } from "@/hooks/use-mobile";
import { loadStripe } from '@stripe/stripe-js';
import { getFunnel, updateFunnel } from "@/lib/db";
import { toast } from "sonner";
import type { FunnelData } from "@/components/FunnelBuilder/types";
import { BuilderHeader } from "@/components/FunnelBuilder/BuilderHeader";
import { MobileBuilder } from "@/components/FunnelBuilder/MobileBuilder";
import { DesktopBuilder } from "@/components/FunnelBuilder/DesktopBuilder";
import { PublishDialog } from "@/components/FunnelBuilder/PublishDialog";

const FunnelBuilder = () => {
  const { slug } = useParams();
  const location = useLocation();
  const isMobileOrTablet = useIsMobile();
  const [funnelData, setFunnelData] = useState<FunnelData>(() => {
    if (location.state?.funnelData) {
      const initialData = {
        ...defaultFunnelData,
        ...location.state.funnelData
      };
      const mainPage = initialData.pages.find(page => page.isMainPage);
      if (mainPage && !initialData.finishPageId) {
        initialData.finishPageId = mainPage.id;
        initialData.afterSubmit = {
          ...initialData.afterSubmit,
          buttonUrl: `/${mainPage.slug}`
        };
      }
      return initialData;
    }
    return defaultFunnelData;
  });
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  const handleLoadTemplate = (template: FunnelData) => {
    setFunnelData(template);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'headerImage') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        if (type === 'logo') {
          setFunnelData(prev => ({ ...prev, logo: base64String }));
        } else if (type === 'headerImage') {
          setFunnelData(prev => ({ ...prev, headerImage: base64String }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!slug) {
      toast.error("Cannot save: No funnel ID provided");
      return;
    }

    setIsSaving(true);
    try {
      await updateFunnel(slug, funnelData);
      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Error saving funnel:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const configDoc = await getDoc(doc(db, 'config', 'stripe'));
      if (!configDoc.exists()) {
        throw new Error('Stripe configuration not found');
      }
      
      const config = configDoc.data();
      if (!config?.publishableKey || !config?.priceId) {
        throw new Error('Invalid Stripe configuration');
      }

      const stripe = await loadStripe(config.publishableKey);
      if (!stripe) throw new Error('Failed to load Stripe');

      const { error } = await stripe.redirectToCheckout({
        lineItems: [{
          price: config.priceId,
          quantity: 1
        }],
        mode: 'payment',
        successUrl: `${window.location.origin}/payment-success?funnelId=${funnelData.id}`,
        cancelUrl: `${window.location.origin}/pricing`,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error(error instanceof Error ? error.message : "Failed to start checkout process");
    } finally {
      setIsUpgrading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadFunnel = async () => {
      if (!slug) {
        setIsLoading(false);
        return;
      }
      try {
        const funnel = await getFunnel(slug);
        if (funnel && isMounted) {
          console.log("Loaded funnel:", funnel);
          const mainPage = funnel.pages.find(page => page.isMainPage);
          if (mainPage && !funnel.finishPageId) {
            funnel.finishPageId = mainPage.id;
            funnel.afterSubmit = {
              ...funnel.afterSubmit,
              buttonUrl: `/${mainPage.slug}`
            };
          }
          setFunnelData(prev => ({ ...prev, ...funnel }));
        }
      } catch (error) {
        console.error("Error loading funnel:", error);
        if (isMounted) {
          toast.error("Failed to load funnel");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFunnel();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="p-8">
          <Skeleton className="h-[600px]" />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <BuilderHeader
        funnelData={funnelData}
        onLoadTemplate={handleLoadTemplate}
        isPro={funnelData.isPro || false}
        isUpgrading={isUpgrading}
        isSaving={isSaving}
        isPublished={funnelData.isPublished || false}
        onUpgrade={handleUpgrade}
        onPublish={() => setIsPublishDialogOpen(true)}
        onSave={handleSave}
      />

      {isMobileOrTablet ? (
        <MobileBuilder
          funnelData={funnelData}
          selectedPageId={selectedPageId}
          onUpdate={setFunnelData}
          onPageSelect={setSelectedPageId}
          handleImageUpload={handleImageUpload}
        />
      ) : (
        <DesktopBuilder
          funnelData={funnelData}
          selectedPageId={selectedPageId}
          onUpdate={setFunnelData}
          onPageSelect={setSelectedPageId}
          handleImageUpload={handleImageUpload}
        />
      )}

      <PublishDialog
        funnelId={slug || ""}
        isOpen={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        isPublished={funnelData.isPublished || false}
        currentSubdomain={funnelData.subdomain}
        isPro={funnelData.isPro} 
        onUpdate={(updatedData) => setFunnelData(prev => ({ ...prev, ...updatedData }))}
      />
    </div>
  );
};

export default FunnelBuilder;
